import { useAuthStore } from '@/stores/authStore';
import { usePricingData } from './usePricingData';
import { useMemo } from 'react';
import type { LifetimeCapLimits } from '@/services/pricingService';

export interface VideoCountInfo {
  videoCount: number;
  limit: number;
  label: string;
  planType: 'rate_based' | 'lifetime_cap';
  isLoading: boolean;
}

/**
 * Hook to get the current video count from subscription data
 * Returns different counts based on plan type:
 * - Rate-based plans: current_period_video_count (resets monthly)
 * - Lifetime cap plans: active_videos_used (never resets)
 */
export const useCurrentMonthVideoCount = (): VideoCountInfo => {
  const { userData, loading } = useAuthStore();
  const { pricingData, loading: pricingLoading } = usePricingData();

  return useMemo(() => {
    const isLoading = loading || pricingLoading || !userData;

    if (isLoading) {
      return {
        videoCount: 0,
        limit: 0,
        label: 'Videos',
        planType: 'rate_based' as const,
        isLoading: true,
      };
    }

    // Determine plan type from subscription or pricing data
    let planType: 'rate_based' | 'lifetime_cap' = 'rate_based';
    let limit = 0;

    if (userData?.subscription) {
      const currentPriceId = userData.subscription.price_id;
      const currentProductId = userData.subscription.product_id;

      // Find the product matching the subscription (by Stripe fields)
      const product = pricingData
        ? Object.values(pricingData).find(
            (tier) =>
              (currentPriceId &&
                (tier.prices?.month?.stripePriceId === currentPriceId ||
                  tier.prices?.year?.stripePriceId === currentPriceId)) ||
              (currentProductId && tier.product_id === currentProductId),
          )
        : null;

      if (product) {
        // Found a matching product - use its plan type
        planType = product.plan_type || userData.subscription.plan_type || 'rate_based';

        if (planType === 'lifetime_cap') {
          limit =
            userData.subscription.max_active_videos || (product.limits as LifetimeCapLimits)?.max_active_videos || 0;
        } else {
          limit = product.monthlyVideos || 0;
        }
      } else {
        // No matching product found - check subscription's plan_type directly
        // This handles lifetime cap plans which don't have Stripe price_id/product_id
        planType = userData.subscription.plan_type || 'rate_based';

        if (planType === 'lifetime_cap') {
          limit = userData.subscription.max_active_videos || 0;
        }
      }
    }

    // Return appropriate count based on plan type
    if (planType === 'lifetime_cap') {
      return {
        videoCount: userData?.subscription?.active_videos_used || 0,
        limit,
        label: 'Active Videos',
        planType,
        isLoading: false,
      };
    } else {
      // Rate-based plan - use current period count
      return {
        videoCount: userData?.subscription?.current_period_video_count || 0,
        limit,
        label: 'Videos This Month',
        planType,
        isLoading: false,
      };
    }
  }, [userData, pricingData, loading, pricingLoading]);
};
